<template>
    <LoadingPage v-if="isLoading"></LoadingPage>

    <!-- ── Summary Cards ── -->
    <div class="cron-summary-row">
        <div class="cron-stat-card">
            <div class="cron-stat-icon" style="background: linear-gradient(135deg, #00A0C1, #33b5d1);">
                <i class="fas fa-layer-group"></i>
            </div>
            <div class="cron-stat-body">
                <span class="cron-stat-value">{{ allJobs.length }}</span>
                <span class="cron-stat-label">Total Jobs</span>
            </div>
        </div>
        <div class="cron-stat-card">
            <div class="cron-stat-icon" style="background: linear-gradient(135deg, #10b981, #34d399);">
                <i class="fas fa-check-circle"></i>
            </div>
            <div class="cron-stat-body">
                <span class="cron-stat-value">{{ activeJobCount }}</span>
                <span class="cron-stat-label">Active</span>
            </div>
        </div>
        <div class="cron-stat-card">
            <div class="cron-stat-icon" style="background: linear-gradient(135deg, #f59e0b, #fbbf24);">
                <i class="fas fa-pause-circle"></i>
            </div>
            <div class="cron-stat-body">
                <span class="cron-stat-value">{{ inactiveJobCount }}</span>
                <span class="cron-stat-label">Inactive</span>
            </div>
        </div>
        <div class="cron-stat-card">
            <div class="cron-stat-icon" style="background: linear-gradient(135deg, #3b82f6, #60a5fa);">
                <i class="fas fa-clock"></i>
            </div>
            <div class="cron-stat-body">
                <span class="cron-stat-value cron-stat-value--small">{{ lastGlobalRun }}</span>
                <span class="cron-stat-label">Last Execution</span>
            </div>
        </div>
    </div>

    <!-- ── Main Table Card ── -->
    <div class="cron-card">
        <div class="cron-card__toolbar">
            <div class="cron-search-wrap">
                <i class="fas fa-search cron-search-icon"></i>
                <input v-model.trim="search" type="text" class="cron-search-input"
                    placeholder="Search jobs…"
                    @keyup="onSearch()" />
            </div>

            <div class="cron-toolbar-right">
                <div class="cron-toolbar-group">
                    <span class="cron-toolbar-label">Sort</span>
                    <select v-model="sortBy" class="cron-select">
                        <option v-for="sortOption in sortByList" :value="sortOption.value" :key="sortOption.value">
                            {{ sortOption.text }}
                        </option>
                    </select>
                </div>
                <div class="cron-toolbar-group">
                    <span class="cron-toolbar-label">Rows</span>
                    <select v-model="pagination.pageSize" class="cron-select cron-select--narrow">
                        <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                        </option>
                    </select>
                </div>

                <div class="cron-pagination">
                    <button @click="previousPage()" :disabled="pagination.page === 1" class="cron-page-btn">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="cron-page-label">
                        <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                    </span>
                    <button @click="nextPage()" :disabled="pagination.page === pagination.totalPages"
                        class="cron-page-btn">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Table -->
        <div class="cron-table-wrap">
            <table class="cron-table">
                <thead>
                    <tr>
                        <th v-for="header in headers" :key="header.value">{{ header.text }}</th>
                    </tr>
                </thead>
                <tbody v-if="!jobsList.length">
                    <tr>
                        <td :colspan="headers.length" class="cron-empty-row">
                            <div class="cron-empty-state">
                                <i class="fas fa-inbox"></i>
                                <span>No records to display.</span>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="job in jobsList" :key="job.id">
                        <td class="cron-cell-name">{{ job.job_name }}</td>
                        <td><code class="cron-job-id">{{ job.job_id }}</code></td>
                        <td class="cron-cell-desc">{{ job.job_description }}</td>
                        <td>
                            <span class="cron-schedule-pill">
                                <i class="far fa-calendar-alt"></i>
                                {{ getReadableSchedule(job.schedule) }}
                            </span>
                        </td>
                        <td>
                            <label class="cron-toggle">
                                <input type="checkbox" v-model="job.is_auto_run" :true-value="1" :false-value="0"
                                    @change="toggleJobStatus(job)">
                                <span class="cron-toggle__track">
                                    <span class="cron-toggle__knob"></span>
                                </span>
                                <span :class="['cron-toggle__label', job.is_auto_run ? 'cron-toggle__label--on' : 'cron-toggle__label--off']">
                                    {{ job.is_auto_run ? 'On' : 'Off' }}
                                </span>
                            </label>
                        </td>
                        <td>
                            <span v-if="job.last_run" class="cron-last-run">{{ formatLastRun(job.last_run) }}</span>
                            <span v-else class="cron-never-run">Never</span>
                        </td>
                        <td class="cron-actions">
                            <button type="button" class="cron-action-btn cron-action-btn--edit"
                                title="Edit Job" @click="openEditModal(job)">
                                <i class="fas fa-pencil-alt"></i>
                            </button>
                            <button type="button" class="cron-action-btn cron-action-btn--run"
                                title="Execute Now" @click="executeJob(job.job_id)">
                                <i class="fas fa-play"></i>
                            </button>
                            <button type="button" class="cron-action-btn cron-action-btn--history"
                                title="View History" @click="openHistoryModal(job.job_id)">
                                <i class="fas fa-history"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- ── Edit Modal ── -->
    <Transition name="cron-modal-fade">
        <div v-if="isModalOpen" class="cron-modal-overlay" @click.self="isModalOpen = false">
            <div class="cron-modal">
                <div class="cron-modal__header">
                    <h5 class="cron-modal__title"><i class="fas fa-cog"></i> Edit Cron Job</h5>
                    <button @click="isModalOpen = false" class="cron-modal__close">&times;</button>
                </div>
                <div class="cron-modal__divider"></div>
                <form @submit.prevent="saveJob" class="cron-modal__body">
                    <div class="cron-form-group">
                        <label for="jobName" class="cron-form-label">Job Name</label>
                        <input type="text" id="jobName" class="cron-form-input cron-form-input--disabled" v-model="currentJob.job_name" disabled>
                    </div>
                    <div class="cron-form-group">
                        <label for="jobId" class="cron-form-label">Job ID</label>
                        <input type="text" id="jobId" class="cron-form-input cron-form-input--disabled" v-model="currentJob.job_id" disabled>
                    </div>
                    <div class="cron-form-group">
                        <label for="jobDescription" class="cron-form-label">Description <span class="cron-required">*</span></label>
                        <textarea id="jobDescription" class="cron-form-input cron-form-textarea" v-model.trim="currentJob.job_description"
                            rows="3" placeholder="e.g., Checks proposals that needs reminder" required></textarea>
                    </div>
                    <div class="cron-form-row">
                        <div class="cron-form-group cron-form-group--half">
                            <label for="scheduleType" class="cron-form-label">Schedule Type <span class="cron-required">*</span></label>
                            <select id="scheduleType" class="cron-form-input" v-model="selectedScheduleType" required>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly (Sundays)</option>
                                <option value="monthly">Monthly (1st day)</option>
                            </select>
                        </div>
                        <div class="cron-form-group cron-form-group--half">
                            <label for="scheduledTime" class="cron-form-label">Scheduled Time (UTC) <span class="cron-required">*</span></label>
                            <input type="time" id="scheduledTime" class="cron-form-input" v-model="scheduledTime" required>
                        </div>
                    </div>
                    <div class="cron-modal__actions">
                        <button type="button" class="cron-btn cron-btn--ghost" @click="isModalOpen = false">Cancel</button>
                        <button type="submit" class="cron-btn cron-btn--primary">
                            <i class="fas fa-save"></i> Update Job
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </Transition>

    <!-- ── History Modal ── -->
    <Transition name="cron-modal-fade">
        <div v-if="isHistoryModalOpen" class="cron-modal-overlay" @click.self="closeHistoryModal">
            <div class="cron-modal cron-modal--wide">
                <div class="cron-modal__header">
                    <h5 class="cron-modal__title"><i class="fas fa-history"></i> Execution History — {{ historyJobName }}</h5>
                    <button @click="closeHistoryModal" class="cron-modal__close">&times;</button>
                </div>
                <div class="cron-modal__divider"></div>
                <div class="cron-modal__body">
                    <div v-if="historyLoading" class="cron-history-loading">
                        <i class="fas fa-spinner fa-spin"></i> Loading history…
                    </div>
                    <div v-else-if="!jobExecutionHistory.length" class="cron-empty-state cron-empty-state--compact">
                        <i class="fas fa-inbox"></i>
                        <span>No execution history found.</span>
                    </div>
                    <div v-else class="cron-history-scroll">
                        <table class="cron-table cron-table--history">
                            <thead>
                                <tr>
                                    <th>Execution Date</th>
                                    <th>Type</th>
                                    <th>Executed By</th>
                                    <th class="text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="record in jobExecutionHistory" :key="record.id">
                                    <td>{{ formatLastRun(record.executed_timestamp) }}</td>
                                    <td>
                                        <span :class="['cron-type-badge', record.is_manual === 1 ? 'cron-type-badge--manual' : 'cron-type-badge--auto']">
                                            <i :class="record.is_manual === 1 ? 'fas fa-hand-pointer' : 'fas fa-robot'"></i>
                                            {{ getExecutionType(record) }}
                                        </span>
                                    </td>
                                    <td>{{ getExecutedByUsername(record.executed_by) }}</td>
                                    <td class="text-center">
                                        <span :class="['cron-status-badge', record.is_success ? 'cron-status-badge--success' : 'cron-status-badge--fail']">
                                            <i :class="record.is_success ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                                            {{ record.is_success ? 'Success' : 'Failed' }}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </Transition>

    <ConfirmModal
       v-model="showConfirmModal"
       :title="confirmModalTitle"
       :message="confirmModalMessage"
       @confirm="onConfirmAction"
    />
</template>

<script>
import { debounce } from '@/plugins/helper.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import axios from 'axios';

export default {
    components: {
        LoadingPage,
    },
    data() {
        return {
            isHistoryModalOpen: false,
            jobExecutionHistory: [],
            historyJobName: '',
            historyLoading: false,
            isLoading: false,
            isModalOpen: false,
            search: "",
            headers: [
                { text: "Job Name", value: "job_name" },
                { text: "Job ID", value: "job_id" },
                { text: "Description", value: "job_description" },
                { text: "Schedule (Local)", value: "schedule" },
                { text: "Auto Run", value: "is_auto_run" },
                { text: "Last Run (Local)", value: "last_run" },
                { text: "Action", value: "action" },
            ],
            allJobs: [],
            jobsList: [],
            currentJob: {
                id: null,
                job_id: "",
                job_name: "",
                job_description: "",
                schedule: "",
                is_auto_run: 1,
                last_run: null,
            },
            selectedScheduleType: 'daily',
            scheduledTime: '00:00',
            pagination: {
                pageSize: 5,
                page: 1,
                totalItems: 0,
                totalPages: 1,
            },
            rowsPerPageList: [{ text: "5", value: 5 }, { text: "10", value: 10 }, { text: "20", value: 20 }],
            sortByList: [
                { text: "Newest", value: "date_desc" },
                { text: "Oldest", value: "date_asc" },
                { text: "ID (A-Z)", value: "id_asc" }
            ],
            sortBy: "date_desc",
            debounceFn: null,
            showConfirmModal: false,
            confirmModalTitle: '',
            confirmModalMessage: '',
            pendingJobId: null,
        };
    },
    computed: {
        paginationLabel() {
            const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
            const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
            return `${start} - ${end}`;
        },
        vtxUsers() {
            return this.$store.state.auth.usersWithVertexRoles || [];
        },
        activeJobCount() {
            return this.allJobs.filter(j => j.is_auto_run === 1).length;
        },
        inactiveJobCount() {
            return this.allJobs.filter(j => j.is_auto_run !== 1).length;
        },
        lastGlobalRun() {
            const runs = this.allJobs.filter(j => j.last_run).map(j => new Date(j.last_run));
            if (!runs.length) return 'Never';
            const latest = new Date(Math.max(...runs));
            const now = new Date();
            const diffMs = now - latest;
            const diffMins = Math.floor(diffMs / 60000);
            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            const diffHours = Math.floor(diffMins / 60);
            if (diffHours < 24) return `${diffHours}h ago`;
            const diffDays = Math.floor(diffHours / 24);
            return `${diffDays}d ago`;
        }
    },
    created() {
        this.init();
        this.debounceFn = debounce(() => this.processClientSideData(), 500);
    },
    watch: {
        'pagination.pageSize'() { this.pagination.page = 1; this.processClientSideData(); },
        sortBy() { this.processClientSideData(); },
        'pagination.page'() { this.processClientSideData(); }
    },
    methods: {
        async init() {
            this.isLoading = true;
            try {
                const response = await axios.get('/jobs');
                if (response.data.success) {
                    this.allJobs = response.data.jobs || [];
                    this.processClientSideData();
                } else {
                    this.$toast.error(response.data.message || "Failed to fetch cron job list.");
                }
            } catch (error) {
                console.error("Failed to fetch cron job list:", error);
                this.$toast.error("An error occurred while fetching the cron job list.");
            } finally {
                this.isLoading = false;
            }
        },
        processClientSideData() {
            let processedList = [...this.allJobs];
            if (this.search) {
                const searchTerm = this.search.toLowerCase();
                processedList = processedList.filter(job =>
                    job.job_id.toLowerCase().includes(searchTerm) ||
                    job.job_name.toLowerCase().includes(searchTerm) ||
                    job.job_description.toLowerCase().includes(searchTerm)
                );
            }
            processedList.sort((a, b) => {
                switch (this.sortBy) {
                    case 'id_asc':
                        return a.job_id.localeCompare(b.job_id);
                    case 'date_desc':
                        if (!a.last_run) return 1;
                        if (!b.last_run) return -1;
                        return new Date(b.last_run) - new Date(a.last_run);
                    case 'date_asc':
                        if (!a.last_run) return 1;
                        if (!b.last_run) return -1;
                        return new Date(a.last_run) - new Date(b.last_run);
                    default:
                        return 0;
                }
            });
            this.pagination.totalItems = processedList.length;
            this.pagination.totalPages = Math.ceil(processedList.length / this.pagination.pageSize);
            if (this.pagination.page > this.pagination.totalPages && this.pagination.totalPages > 0) {
                this.pagination.page = this.pagination.totalPages;
            }
            const start = (this.pagination.page - 1) * this.pagination.pageSize;
            const end = start + this.pagination.pageSize;
            this.jobsList = processedList.slice(start, end);
        },
        onSearch() {
            this.pagination.page = 1;
            this.debounceFn();
        },
        openEditModal(job) {
            this.currentJob = { ...job };
            const cronParts = job.schedule.split(' ');
            const [minutes, hours, dayOfMonth, month, dayOfWeek] = cronParts.slice(1);

            this.scheduledTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;

            if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                this.selectedScheduleType = 'daily';
            } else if (dayOfMonth === '*' && month === '*' && dayOfWeek === '0') {
                this.selectedScheduleType = 'weekly';
            } else if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
                this.selectedScheduleType = 'monthly';
            } else {
                this.selectedScheduleType = 'daily';
            }
            this.isModalOpen = true;
        },
        async saveJob() {
            this.isLoading = true;
            try {
                const payload = {
                    id: this.currentJob.id,
                    job_id: this.currentJob.job_id,
                    job_name: this.currentJob.job_name,
                    job_description: this.currentJob.job_description,
                    schedule: this.generateCronString(),
                    is_auto_run: this.currentJob.is_auto_run
                };
                const response = await axios.post('/jobs', payload);
                if (response.data.success) {
                    this.$toast.success(response.data.message || "Cron job updated successfully!");
                    this.isModalOpen = false;
                    await this.init();
                } else {
                    this.$toast.error(response.data.message || "Failed to update cron job.");
                }
            } catch (error) {
                console.error("Failed to save cron job:", error);
                this.$toast.error("An error occurred while saving the cron job.");
            } finally {
                this.isLoading = false;
            }
        },
        async toggleJobStatus(job) {
            this.isLoading = true;
            try {
                const payload = {
                    id: job.id,
                    job_id: job.job_id,
                    job_name: job.job_name,
                    job_description: job.job_description,
                    schedule: job.schedule,
                    is_auto_run: job.is_auto_run
                };
                const response = await axios.post('/jobs', payload);
                if (response.data.success) {
                    this.$toast.success(response.data.message || "Cron job status updated successfully.");
                    await this.init();
                } else {
                    job.is_auto_run = job.is_auto_run === 1 ? 0 : 1;
                    this.$toast.error(response.data.message || "Failed to update job status.");
                }
            } catch (error) {
                job.is_auto_run = job.is_auto_run === 1 ? 0 : 1;
                console.error("Failed to toggle job status:", error);
                this.$toast.error("An error occurred while updating the job status.");
            } finally {
                this.isLoading = false;
            }
        },
        executeJob(jobId) {
            this.pendingJobId = jobId;
            this.confirmModalTitle = 'Execute Cron Job';
            this.confirmModalMessage = `Are you sure you want to execute job "${jobId}" now?`;
            this.showConfirmModal = true;
        },
        async onConfirmAction() {
            const jobId = this.pendingJobId;
            this.pendingJobId = null;
            if (!jobId) return;
            this.isLoading = true;
            try {
                const payload = {
                    job_id: jobId
                };
                const response = await axios.post('/jobs/execute', payload);
                if (response.data.success) {
                    this.$toast.success(response.data.message || `Job "${jobId}" triggered successfully.`);
                    await this.init();
                } else {
                    this.$toast.error(response.data.message || `Failed to trigger job "${jobId}".`);
                }
            } catch (error) {
                console.error(`Failed to execute job "${jobId}":`, error);
                this.$toast.error("An error occurred while executing the job.");
            } finally {
                this.isLoading = false;
            }
        },
        formatLastRun(timestamp) {
            if (!timestamp) return 'Never';
            const date = new Date(timestamp);
            return date.toLocaleString();
        },
        getReadableSchedule(schedule) {
            const parts = schedule.split(' ');
            if (parts.length < 6) return schedule;

            const [minutesStr, hoursStr, dayOfMonth, month, dayOfWeek] = parts.slice(1);

            const isMinuteInterval = minutesStr.startsWith('*/');
            const isHourInterval = hoursStr.startsWith('*/');

            if (isMinuteInterval && hoursStr === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                const interval = parseInt(minutesStr.split('/')[1], 10);
                return `Every ${interval} minute${interval > 1 ? 's' : ''}`;
            }
            if (isHourInterval && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                const interval = parseInt(hoursStr.split('/')[1], 10);
                const minPart = minutesStr === '*' || minutesStr === '0' ? '' : ` at :${minutesStr.padStart(2, '0')}`;
                return `Every ${interval} hour${interval > 1 ? 's' : ''}${minPart}`;
            }

            const hoursUTC = hoursStr === '*' ? 0 : parseInt(hoursStr, 10);
            const minutesUTC = minutesStr === '*' ? 0 : parseInt(minutesStr, 10);

            if (isNaN(hoursUTC) || isNaN(minutesUTC)) return schedule;

            const date = new Date();
            date.setUTCDate(15);
            date.setUTCHours(hoursUTC, minutesUTC, 0, 0);

            const localHours = date.getHours().toString().padStart(2, '0');
            const localMinutes = date.getMinutes().toString().padStart(2, '0');
            const localTime = `${localHours}:${localMinutes}`;

            const dayOffset = date.getDate() - 15;

            if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
                return `Daily at ${localTime}`;
            } else if (dayOfMonth === '*' && month === '*' && dayOfWeek === '0') {
                const days = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
                const localDayIndex = (0 + dayOffset + 7) % 7;
                return `Weekly on ${days[localDayIndex]} at ${localTime}`;
            } else if (dayOfMonth === '1' && month === '*' && dayOfWeek === '*') {
                let localDayOfMonth = 1 + dayOffset;
                let dayString;
                if (localDayOfMonth === 0) {
                    dayString = "the last day of the month";
                } else if (localDayOfMonth === 1) {
                    dayString = "the 1st";
                } else if (localDayOfMonth === 2) {
                    dayString = "the 2nd";
                } else {
                    dayString = `the ${localDayOfMonth}th`;
                }
                return `Monthly on ${dayString} at ${localTime}`;
            } else {
                return schedule;
            }
        },
        generateCronString() {
            const [hours, minutes] = this.scheduledTime.split(':');
            switch (this.selectedScheduleType) {
                case 'daily':
                    return `0 ${minutes} ${hours} * * *`;
                case 'weekly':
                    return `0 ${minutes} ${hours} * * 0`;
                case 'monthly':
                    return `0 ${minutes} ${hours} 1 * *`;
                default:
                    return `0 ${minutes} ${hours} * * *`;
            }
        },
        previousPage() { if (this.pagination.page > 1) { this.pagination.page--; } },
        nextPage() { if (this.pagination.page < this.pagination.totalPages) { this.pagination.page++; } },
        getExecutedByUsername(userId) {
            if (!userId) return 'System';
            if (!this.vtxUsers || this.vtxUsers.length === 0) return 'Loading...';
            const user = Array.isArray(this.vtxUsers) ? this.vtxUsers.find(u => u && u.id === userId) : null;
            return user ? `${user.first_name} ${user.last_name}` : 'Deleted User';
        },
        async openHistoryModal(jobId) {
            this.historyLoading = true;
            this.isHistoryModalOpen = true;
            this.historyJobName = this.allJobs.find(j => j.job_id === jobId).job_name;
            try {
                const response = await this.$axios.get(`/jobs/${jobId}/history`);
                if (response.data.success) {
                    this.jobExecutionHistory = response.data.history || [];
                } else {
                    this.$toast.error(response.data.message || "Failed to fetch job history.");
                }
            } catch (error) {
                console.error("Failed to fetch job history:", error);
                this.$toast.error("An error occurred while fetching the job history.");
            } finally {
                this.historyLoading = false;
            }
        },
        getExecutionType(record) {
            return record.is_manual === 1 ? 'Manual' : 'Automated';
        },
        closeHistoryModal() {
            this.isHistoryModalOpen = false;
            this.jobExecutionHistory = [];
        },
        getVertexUsers() {
            this.vtxUsers = this.$store.state.auth.usersWithVertexRoles || [];
        },
    }
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   CRON DASHBOARD — Premium Redesign
   ═══════════════════════════════════════════════════════════ */

/* ── Summary Cards Row ── */
.cron-summary-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 20px;
}

.cron-stat-card {
    background: #fff;
    border-radius: 14px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.cron-stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.cron-stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 1.15rem;
    flex-shrink: 0;
}

.cron-stat-body {
    display: flex;
    flex-direction: column;
}

.cron-stat-value {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1e293b;
    line-height: 1.2;
}

.cron-stat-value--small {
    font-size: 1.1rem;
}

.cron-stat-label {
    font-size: 0.8rem;
    color: #94a3b8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
}

/* ── Main Card ── */
.cron-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    overflow: visible;
}

/* ── Toolbar ── */
.cron-card__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
}

.cron-search-wrap {
    position: relative;
    min-width: 240px;
}

.cron-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.85rem;
}

.cron-search-input {
    width: 100%;
    padding: 9px 12px 9px 36px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.875rem;
    color: #334155;
    background: #f8fafc;
    transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
    outline: none;
}

.cron-search-input:focus {
    border-color: #00A0C1;
    box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
    background: #fff;
}

.cron-toolbar-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
}

.cron-toolbar-group {
    display: flex;
    align-items: center;
    gap: 6px;
}

.cron-toolbar-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.cron-select {
    padding: 6px 28px 6px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #00A0C1;
    font-weight: 600;
    background: #f8fafc;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;
    appearance: auto;
}

.cron-select:focus {
    border-color: #00A0C1;
}

.cron-select--narrow {
    min-width: 80px;
    width: auto;
}

/* ── Pagination ── */
.cron-pagination {
    display: inline-flex;
    align-items: center;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    overflow: visible;
    background: #f8fafc;
}

.cron-page-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #00A0C1;
    font-size: 0.85rem;
    padding: 7px 12px;
    transition: background-color 0.15s;
}

.cron-page-btn:hover:not(:disabled) {
    background: #e6f7fa;
}

.cron-page-btn:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
}

.cron-page-label {
    font-size: 0.82rem;
    color: #64748b;
    font-weight: 500;
    padding: 0 6px;
    white-space: nowrap;
}

.cron-page-label strong {
    color: #1e293b;
}

/* ── Table ── */
.cron-table-wrap {
    overflow-x: auto;
}

.cron-table {
    width: 100%;
    border-collapse: collapse;
}

.cron-table thead tr {
    background: linear-gradient(135deg, #f8fafc 0%, #e6f7fa 100%);
}

.cron-table th {
    padding: 13px 16px;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
}

.cron-table td {
    padding: 14px 16px;
    font-size: 0.875rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.cron-table tbody tr {
    transition: background-color 0.15s ease;
}

.cron-table tbody tr:hover {
    background-color: #f5fcfd;
}

.cron-table tbody tr:last-child td {
    border-bottom: none;
}

.cron-cell-name {
    font-weight: 600;
    color: #1e293b;
}

.cron-cell-desc {
    max-width: 260px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #64748b;
}

.cron-job-id {
    background: #f1f5f9;
    color: #00A0C1;
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

.cron-schedule-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    background: #e6f7fa;
    color: #008da8;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
}

.cron-schedule-pill i {
    font-size: 0.75rem;
}

/* ── Toggle Switch ── */
.cron-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.cron-toggle input {
    display: none;
}

.cron-toggle__track {
    width: 44px;
    height: 24px;
    border-radius: 12px;
    background: #cbd5e1;
    position: relative;
    transition: background-color 0.25s ease;
}

.cron-toggle input:checked + .cron-toggle__track {
    background: linear-gradient(135deg, #10b981, #34d399);
}

.cron-toggle__knob {
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: 3px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    transition: transform 0.25s ease;
}

.cron-toggle input:checked + .cron-toggle__track .cron-toggle__knob {
    transform: translateX(20px);
}

.cron-toggle__label {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.cron-toggle__label--on {
    color: #10b981;
}

.cron-toggle__label--off {
    color: #94a3b8;
}

/* ── Last Run ── */
.cron-last-run {
    font-size: 0.82rem;
    color: #475569;
}

.cron-never-run {
    font-size: 0.82rem;
    color: #cbd5e1;
    font-style: italic;
}

/* ── Action Buttons ── */
.cron-actions {
    text-align: right;
    white-space: nowrap;
}

.cron-action-btn {
    width: 36px;
    height: 36px;
    border-radius: 50% !important;
    border: none;
    cursor: pointer;
    font-size: 15px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 6px;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.cron-action-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.cron-action-btn:active {
    transform: translateY(0);
}

.cron-action-btn--edit {
    background: #e6f7fa;
    color: #00A0C1;
}

.cron-action-btn--edit:hover {
    background: #00A0C1;
    color: #fff;
}

.cron-action-btn--run {
    background: #ecfdf5;
    color: #10b981;
}

.cron-action-btn--run:hover {
    background: #10b981;
    color: #fff;
}

.cron-action-btn--history {
    background: #f0f9ff;
    color: #3b82f6;
}

.cron-action-btn--history:hover {
    background: #3b82f6;
    color: #fff;
}

/* ── Empty State ── */
.cron-empty-row {
    text-align: center;
    padding: 48px 16px !important;
}

.cron-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #94a3b8;
}

.cron-empty-state i {
    font-size: 2rem;
    color: #cbd5e1;
}

.cron-empty-state span {
    font-size: 0.9rem;
}

.cron-empty-state--compact {
    padding: 32px 16px;
}

/* ═══════════════════════════════════════════════════════════
   MODAL SYSTEM
   ═══════════════════════════════════════════════════════════ */

.cron-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
}

.cron-modal {
    background: #fff;
    border-radius: 20px;
    width: 100%;
    max-width: 560px;
    min-width: 340px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    animation: cron-modal-enter 0.25s ease-out;
}

.cron-modal--wide {
    max-width: 780px;
}

@keyframes cron-modal-enter {
    from {
        opacity: 0;
        transform: translateY(12px) scale(0.98);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.cron-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 0;
}

.cron-modal__title {
    font-weight: 700;
    font-size: 1.15rem;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
}

.cron-modal__title i {
    color: #00A0C1;
}

.cron-modal__close {
    background: none;
    border: none;
    font-size: 1.8rem;
    color: #94a3b8;
    cursor: pointer;
    line-height: 1;
    padding: 0;
    transition: color 0.15s;
}

.cron-modal__close:hover {
    color: #ef4444;
}

.cron-modal__divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
    margin: 16px 24px 0;
}

.cron-modal__body {
    padding: 20px 24px 24px;
}

.cron-modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
}

/* ── Form Controls ── */
.cron-form-group {
    margin-bottom: 16px;
}

.cron-form-row {
    display: flex;
    gap: 16px;
}

.cron-form-group--half {
    flex: 1;
}

.cron-form-label {
    display: block;
    font-weight: 600;
    font-size: 0.82rem;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.cron-required {
    color: #ef4444;
}

.cron-form-input {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.875rem;
    color: #334155;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
}

.cron-form-input:focus {
    border-color: #00A0C1;
    box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
}

.cron-form-input--disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
}

.cron-form-textarea {
    resize: vertical;
    min-height: 80px;
}

/* ── Buttons ── */
.cron-btn {
    padding: 10px 22px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    border: none;
}

.cron-btn--primary {
    background: linear-gradient(135deg, #00A0C1, #33b5d1);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.cron-btn--primary:hover {
    background: linear-gradient(135deg, #008da8, #00A0C1);
    box-shadow: 0 4px 16px rgba(0, 160, 193, 0.4);
    transform: translateY(-1px);
}

.cron-btn--ghost {
    background: transparent;
    color: #64748b;
    border: 1.5px solid #e2e8f0;
}

.cron-btn--ghost:hover {
    background: #f8fafc;
    color: #334155;
}

/* ── History ── */
.cron-history-loading {
    text-align: center;
    padding: 32px;
    color: #00A0C1;
    font-size: 0.9rem;
}

.cron-history-loading i {
    margin-right: 6px;
}

.cron-history-scroll {
    max-height: 400px;
    overflow-y: auto;
}

.cron-table--history thead {
    position: sticky;
    top: 0;
    z-index: 1;
}

/* ── Badges ── */
.cron-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 16px;
    font-size: 0.78rem;
    font-weight: 600;
}

.cron-type-badge--auto {
    background: #ecfdf5;
    color: #059669;
}

.cron-type-badge--manual {
    background: #fef3c7;
    color: #b45309;
}

.cron-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 700;
    font-size: 0.82rem;
}

.cron-status-badge--success {
    color: #10b981;
}

.cron-status-badge--fail {
    color: #ef4444;
}

/* ── Modal Transition ── */
.cron-modal-fade-enter-active,
.cron-modal-fade-leave-active {
    transition: opacity 0.2s ease;
}

.cron-modal-fade-enter-from,
.cron-modal-fade-leave-to {
    opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 992px) {
    .cron-summary-row {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 576px) {
    .cron-summary-row {
        grid-template-columns: 1fr;
    }

    .cron-card__toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .cron-search-wrap {
        min-width: unset;
    }

    .cron-toolbar-right {
        justify-content: space-between;
    }

    .cron-form-row {
        flex-direction: column;
        gap: 0;
    }
}

.text-center {
    text-align: center;
}
</style>